import React from 'react';
import type { ClientInfo, LatencyMeasurement, TraceResult } from '../types/diagnostics';
import { formatLatency } from '../utils/statistics';

interface Props {
  clientInfo: ClientInfo | null;
  latencies: LatencyMeasurement[];
  trace: TraceResult | null;
}

const latencyLabels: Record<string, string> = {
  http: 'HTTP',
  tcp: 'TCP Handshake',
  tls: 'TLS Handshake',
  dns: 'DNS Resolve',
  ws: 'WebSocket',
  webrtc: 'WebRTC ICE',
};

export const NetworkInfo: React.FC<Props> = ({ clientInfo, latencies, trace }) => {
  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
        Network Information
      </h3>

      <div className="space-y-4">
        {/* Client Info */}
        {clientInfo && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-slate-500 uppercase">Connection</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <InfoRow label="IP Address" value={clientInfo.ip} />
              <InfoRow label="Protocol" value={clientInfo.ipv6 ? 'IPv6' : 'IPv4'} />
              <InfoRow label="HTTP Version" value={clientInfo.proto} />
              {clientInfo.geo && (
                <>
                  <InfoRow label="Location" value={`${clientInfo.geo.city}, ${clientInfo.geo.country}`} />
                  <InfoRow label="ISP" value={clientInfo.geo.isp} />
                  <InfoRow label="Timezone" value={clientInfo.geo.timezone} />
                </>
              )}
              {clientInfo.asn && clientInfo.asn.asn > 0 && (
                <InfoRow label="ASN" value={`AS${clientInfo.asn.asn} — ${clientInfo.asn.name}`} />
              )}
            </div>
          </div>
        )}

        {/* Latency breakdown */}
        {latencies.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-slate-500 uppercase">Latency Breakdown</h4>
            <div className="space-y-1">
              {latencies.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-28">{latencyLabels[l.type] ?? l.type}</span>
                  <div className="flex-1 bg-dark-850 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-end px-1.5"
                      style={{
                        width: `${Math.min(100, (l.durationMs / Math.max(...latencies.map((x) => x.durationMs))) * 100)}%`,
                        minWidth: '20%',
                      }}
                    >
                      <span className="text-[10px] font-medium text-white">
                        {formatLatency(l.durationMs)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DNS Info */}
        {trace?.dns && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-slate-500 uppercase">DNS</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <InfoRow label="Resolve Time" value={formatLatency(trace.dns.resolveMs)} />
              {trace.dns.reverse && trace.dns.reverse.length > 0 && (
                <InfoRow label="Reverse DNS" value={trace.dns.reverse[0]} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[10px] text-slate-500 uppercase">{label}</span>
    <span className="text-xs text-white truncate" title={value}>{value}</span>
  </div>
);
